import { createFileRoute } from "@tanstack/react-router";
import { Section, SectionHeading, Eyebrow, TrustBar } from "@/components/sections";
import { QuoteForm } from "@/components/QuoteForm";
import { SITE, breadcrumbJsonLd } from "@/lib/site";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Anchor Services — Get a Free Home Watch Quote in Sarasota" },
      { name: "description", content: "Get a free quote for Sarasota Home Watch, junk removal, cleanouts or concierge services. Same-day response, no obligation, fully licensed and insured." },
      { property: "og:title", content: "Contact Anchor Services" },
      { property: "og:description", content: "Free quote for Sarasota Home Watch and property care." },
      { property: "og:url", content: "https://anchorsrq.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://anchorsrq.com/contact" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd([{ name: 'Home', url: 'https://anchorsrq.com/' }, { name: 'Contact', url: 'https://anchorsrq.com/contact' }])) },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <>
      <Section>
        <SectionHeading
          eyebrow="Contact"
          title="Tell us about your Sarasota property."
          intro="Share a few details and we'll send a custom proposal — typically within one business day. Prefer to talk? Call us anytime during business hours."
        />
        <div className="mt-12 grid gap-12 lg:grid-cols-5">
          <div className="lg:col-span-2 space-y-5">
            <ContactRow icon={Phone} label="Phone" value={SITE.phone} href={SITE.phoneHref} />
            <ContactRow icon={Mail} label="Email" value={SITE.email} href={`mailto:${SITE.email}`} />
            <ContactRow icon={MapPin} label="Service Area" value={`${SITE.city}, ${SITE.region} & surrounding areas`} />
            <ContactRow icon={Clock} label="Hours" value={SITE.hours} />
            <div className="overflow-hidden rounded-xl border border-border">
              <iframe
                title="Sarasota service area map"
                src="https://www.google.com/maps?q=Sarasota%2C+FL&output=embed"
                width="100%"
                height="280"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
          <div className="lg:col-span-3">
            <QuoteForm sourcePage="/contact" />
          </div>
        </div>
      </Section>
      <TrustBar />
    </>
  );
}

function ContactRow({ icon: Icon, label, value, href }: { icon: typeof Phone; label: string; value: string; href?: string }) {
  const inner = (
    <>
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[oklch(0.78_0.12_82)]/20 text-[oklch(0.55_0.12_82)]">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-medium text-foreground">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="flex items-center gap-4 rounded-xl border border-border bg-card p-4 transition hover:border-[oklch(0.78_0.12_82)]">
      {inner}
    </a>
  ) : (
    <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">{inner}</div>
  );
}